import React, { useState, useEffect } from 'react';
import { useApiClient } from '../services/api';
import { Box, Button, Card, CardContent, Typography, Alert, CircularProgress, Chip } from '@mui/material';
import { CheckCircle, AccountBalance, Warning } from '@mui/icons-material';

interface ConnectAccountSetupProps {
  onAccountConnected?: () => void;
}

/**
 * ConnectAccountSetup Component
 * Handles Stripe Connect account onboarding flow
 * - Creates Stripe Express account
 * - Redirects to Stripe onboarding
 * - Shows account status and balance
 */
const ConnectAccountSetup: React.FC<ConnectAccountSetupProps> = ({ onAccountConnected }) => {
  const apiClient = useApiClient();

  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [account, setAccount] = useState<any>(null);
  const [balance, setBalance] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch connected account on mount
  useEffect(() => {
    fetchAccount();
  }, []);

  const fetchAccount = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.getCurrentUserConnectedAccount();

      if (response.success && response.data) {
        setAccount(response.data);

        // Fetch balance if account exists
        if (response.data.id) {
          const balanceResponse = await apiClient.getAccountBalance(response.data.id);
          if (balanceResponse.success) {
            setBalance(balanceResponse.data);
          }
        }

        if (onAccountConnected) {
          onAccountConnected();
        }
      }
    } catch (err) {
      // No account yet - this is expected for new users
      console.log('No connected account yet');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async () => {
    setCreating(true);
    setError(null);

    try {
      // Create connected account
      // Use FR (France) as default since platform is EU-based
      // This prevents cross-border transfer restrictions
      const createResponse = await apiClient.createConnectedAccount({
        country: 'FR',
        accountType: 'express',
      });

      if (!createResponse.success) {
        throw new Error(createResponse.message || 'Failed to create account');
      }

      const newAccount = createResponse.data;
      setAccount(newAccount);

      // Create account link for onboarding
      const linkResponse = await apiClient.createAccountLink({
        accountId: newAccount.id,
        refreshUrl: `${window.location.origin}/sponsor/dashboard?tab=payouts&refresh=true`,
        returnUrl: `${window.location.origin}/sponsor/dashboard?tab=payouts&success=true`,
      });

      if (!linkResponse.success || !linkResponse.data?.url) {
        throw new Error('Failed to create onboarding link');
      }

      // Redirect to Stripe onboarding
      window.location.href = linkResponse.data.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set up account');
      setCreating(false);
    }
  };

  const handleRefreshOnboarding = async () => {
    if (!account) return;

    setCreating(true);
    setError(null);

    try {
      const linkResponse = await apiClient.createAccountLink({
        accountId: account.id,
        refreshUrl: `${window.location.origin}/sponsor/dashboard?tab=payouts&refresh=true`,
        returnUrl: `${window.location.origin}/sponsor/dashboard?tab=payouts&success=true`,
      });

      if (!linkResponse.success || !linkResponse.data?.url) {
        throw new Error('Failed to create onboarding link');
      }

      window.location.href = linkResponse.data.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh onboarding');
      setCreating(false);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'usd'): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100); // Convert from cents
  };

  const getStatusColor = (status?: string): 'success' | 'warning' | 'error' | 'default' => {
    if (!status) return 'default';
    if (status === 'active') return 'success';
    if (status === 'restricted' || status === 'disabled') return 'error';
    return 'warning';
  };

  const getStatusIcon = (status?: string) => {
    if (status === 'active') return <CheckCircle />;
    if (status === 'restricted' || status === 'disabled') return <Warning />;
    return null;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (!account) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={4}>
            <AccountBalance sx={{ fontSize: 60, color: 'primary.main' }} />
            <Typography variant="h5" gutterBottom>
              Set Up Payouts
            </Typography>
            <Typography variant="body1" color="text.secondary" textAlign="center" maxWidth="500px">
              Connect your bank account to receive bounty payouts. We use Stripe to ensure secure and reliable payments.
            </Typography>

            {/* Region Warning */}
            <Alert severity="info" sx={{ width: '100%', maxWidth: '500px' }}>
              <Typography variant="body2">
                <strong>Important:</strong> Your account will be set up for the EU region (France). Make sure your bank account is in an EU country to avoid transfer restrictions.
              </Typography>
            </Alert>

            {error && (
              <Alert severity="error" sx={{ width: '100%', maxWidth: '500px' }}>
                {error}
              </Alert>
            )}

            <Button
              variant="contained"
              size="large"
              onClick={handleCreateAccount}
              disabled={creating}
              startIcon={creating ? <CircularProgress size={20} /> : <AccountBalance />}
            >
              {creating ? 'Setting up...' : 'Connect Bank Account'}
            </Button>

            <Typography variant="caption" color="text.secondary" textAlign="center" maxWidth="400px">
              You'll be redirected to Stripe to securely connect your bank account. This takes about 2-3 minutes.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const isOnboardingComplete = account.details_submitted && account.payouts_enabled;

  return (
    <Card>
      <CardContent>
        <Box display="flex" flexDirection="column" gap={3}>
          {/* Account Status Header */}
          <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6">
                Payout Account
              </Typography>
              <Chip
                label={account.account_status || 'pending'}
                color={getStatusColor(account.account_status)}
                size="small"
              />
            </Box>

            {!isOnboardingComplete && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Box display="flex" flexDirection="column" gap={1}>
                  <Typography variant="body2">
                    Your account setup is incomplete. Complete the onboarding process to receive payouts.
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleRefreshOnboarding}
                    disabled={creating}
                  >
                    {creating ? 'Loading...' : 'Continue Setup'}
                  </Button>
                </Box>
              </Alert>
            )}
          </Box>

          {/* Balance Section */}
          {balance && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Available Balance
              </Typography>
              <Typography variant="h4" color="primary.main">
                {formatCurrency(
                  balance.available?.[0]?.amount || 0,
                  balance.available?.[0]?.currency || account.currency
                )}
              </Typography>

              {balance.pending && balance.pending[0] && balance.pending[0].amount > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Pending: {formatCurrency(balance.pending[0].amount, balance.pending[0].currency)}
                </Typography>
              )}
            </Box>
          )}

          {/* Account Details */}
          <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Account Type
              </Typography>
              <Typography variant="body2">
                {account.account_type || 'Express'}
              </Typography>
            </Box>

            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Currency
              </Typography>
              <Typography variant="body2">
                {(account.currency || 'USD').toUpperCase()}
              </Typography>
            </Box>

            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Payouts Enabled
              </Typography>
              <Chip
                label={account.payouts_enabled ? 'Yes' : 'No'}
                size="small"
                color={account.payouts_enabled ? 'success' : 'default'}
              />
            </Box>

            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Charges Enabled
              </Typography>
              <Chip
                label={account.charges_enabled ? 'Yes' : 'No'}
                size="small"
                color={account.charges_enabled ? 'success' : 'default'}
              />
            </Box>
          </Box>

          {error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default ConnectAccountSetup;
